// Visitor-billing call-to-action popups (the "dots" funnel).
//   • Q4NudgePopup          — fires on the 4th question: "open the Tools drawer"
//   • FavoriteSignupCTA     — star = commit to a free sign-up, done IN-PLACE
//                             (no page jump), with a nested Home-Screen how-to
//   • OutOfInteractionsWall — pool empty → tip (resets) or subscribe
//   • CeilingExceededWall   — paid sub past their usage ceiling → tip or tokens
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, LockOpen, Star, House, Coins, Lock, ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, saveAuth } from "@/lib/api";
import { getVisitorSig, getRefSig } from "@/lib/attribution";
import WhatIsMySiloModal from "@/components/WhatIsMySiloModal";

const fmtPrice = (cents) => {
  const d = (cents || 0) / 100;
  return `$${d.toFixed(d % 1 === 0 ? 0 : 2)}`;
};

function Shell({ children, onClose, testid, maxW = "max-w-md" }) {
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid={testid}>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ type: "spring", stiffness: 320, damping: 26 }}
        className={`relative w-full ${maxW} bg-[#FDFCF8] rounded-2xl shadow-2xl border border-[#E5E5E0] p-6`}
      >
        {children}
      </motion.div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
export function Q4NudgePopup({ open, siloName, onOpenDrawer, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <Shell onClose={onClose} testid="q4-nudge-popup">
          <button onClick={onClose} data-testid="q4-nudge-close"
            className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]"><X size={18} /></button>
          <div className="w-11 h-11 rounded-full bg-[#EAF1EC] flex items-center justify-center mb-4">
            <LockOpen size={22} weight="duotone" className="text-[#4A5D4E]" />
          </div>
          <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
            Welcome! Unlock more with {siloName || "this silo"}.
          </h3>
          <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
            As a first-time visitor to <strong className="text-[#1C1B1A]">{siloName || "this"}</strong>'s digital
            twin, you've used your first three questions. Great news — you can unlock more right now.
          </p>
          <button
            onClick={onOpenDrawer}
            data-testid="q4-nudge-open-drawer"
            className="mt-5 w-full bg-[#1C1B1A] text-[#FDFCF8] rounded-full py-3 text-sm font-semibold hover:bg-[#3A4A3E] flex items-center justify-center gap-2"
          >
            Open the Tools drawer to see how <ArrowRight size={16} weight="bold" />
          </button>
        </Shell>
      )}
    </AnimatePresence>
  );
}

// ───────────────────────────────────────────────────────────────────────────
export function HomeScreenHowTo({ onClose }) {
  const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" data-testid="homescreen-howto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="modal-safe relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-[#E5E5E0] p-5"
      >
        <button onClick={onClose} data-testid="homescreen-howto-close"
          className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]"><X size={18} /></button>
        <div className="flex items-center gap-2 mb-3">
          <House size={20} weight="duotone" className="text-[#4A5D4E]" />
          <h4 className="text-base font-semibold text-[#1C1B1A]">Add to Home Screen</h4>
        </div>
        <ol className="text-[13px] text-[#3A4A3E] space-y-2 list-decimal pl-4 leading-relaxed">
          {isIOS && (<>
            <li>Tap the <strong>Share</strong> icon in your browser bar.</li>
            <li>Scroll and tap <strong>“Add to Home Screen.”</strong></li>
            <li>Tap <strong>Add</strong> — done!</li>
          </>)}
          {isAndroid && (<>
            <li>Tap the <strong>⋮</strong> menu (top-right).</li>
            <li>Tap <strong>“Add to Home screen” / “Install app.”</strong></li>
            <li>Confirm — done!</li>
          </>)}
          {!isIOS && !isAndroid && (<>
            <li>Click the <strong>install icon</strong> in your browser's address bar.</li>
            <li>Or open the <strong>⋮</strong> menu → <strong>“Install / Create shortcut.”</strong></li>
          </>)}
        </ol>
        <p className="text-[11px] text-[#6B6A68] mt-3">
          Opening the silo from your home screen unlocks another question — and makes it one tap away.
        </p>
        <button onClick={onClose} data-testid="homescreen-howto-done"
          className="mt-4 w-full text-sm rounded-full border border-[#E5E5E0] py-2 hover:border-[#4A5D4E] text-[#1C1B1A]">
          Got it
        </button>
      </motion.div>
    </div>
  );
}

export function FavoriteSignupCTA({
  open, slug, siloName, signedIn, starOnSignup = true, onSignedUp, onAlreadyMemberFilled, onClose,
}) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [howTo, setHowTo] = useState(false);
  const [haha, setHaha] = useState(false);
  const [whatIs, setWhatIs] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.password) return toast.error("Enter an email and password.");
    setBusy(true);
    try {
      const r = await api.post("/auth/signup", {
        name: form.name || form.email.split("@")[0],
        email: form.email,
        password: form.password,
        tos_accepted: true,
        referrer_sig: getRefSig() || undefined,
        from_silo_slug: slug || undefined,
        visitor_sig: getVisitorSig() || undefined,
      });
      saveAuth(r.data.token, r.data.user);
      // Favoriting = the commitment we just honored: star the silo so the
      // "favorite" dot fills too. Skipped when this card is opened as a PURE
      // sign-up (e.g. the header "Sign up" button) — there, favoriting stays a
      // separate action via the star.
      if (starOnSignup) {
        try {
          const s = await api.get(`/s/${slug}`);
          if (s.data?.id) await api.post("/interactions/star", { silo_id: s.data.id, starred: true });
        } catch { /* non-fatal */ }
      }
      toast.success(`Welcome, ${r.data.user.name}! You're in.`);
      onSignedUp?.(r.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't sign you up — try a different email.");
    } finally {
      setBusy(false);
    }
  };

  const alreadyMember = () => {
    if (signedIn) {
      onAlreadyMemberFilled?.();   // role check passes → close + fill
    } else {
      setHaha(true);               // can't verify an anon claim
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Shell onClose={onClose} testid="favorite-signup-cta">
          <button onClick={onClose} data-testid="favorite-signup-close"
            className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]"><X size={18} /></button>
          {/* Oct 4, 2026 (Randy) — most people reach this sheet from SOMEONE
              ELSE'S silo and have no idea what the platform is. One line, not a
              pitch block: the explainer opens as a sibling layer so the form
              below keeps whatever they already typed. */}
          <button onClick={() => setWhatIs(true)} data-testid="favorite-signup-whatis"
            className="text-[11px] uppercase tracking-[0.18em] text-[#C16E5A] hover:text-[#1C1B1A] font-medium inline-flex items-center gap-1 mb-3 transition-colors">
            <Sparkle size={12} weight="duotone" /> What is My Silo?
          </button>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 ${starOnSignup ? "bg-[#FDF1ED]" : "bg-[#EAF1EC]"}`}>
            {starOnSignup
              ? <Star size={22} weight="fill" className="text-[#C16E5A]" />
              : <LockOpen size={22} weight="duotone" className="text-[#4A5D4E]" />}
          </div>
          <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
            {starOnSignup
              ? <>Let {siloName || "this silo"} know you're a fan.</>
              : <>Sign up — it's free.</>}
          </h3>
          <p className="text-sm text-[#6B6A68] mt-1.5">
            {starOnSignup
              ? "Sign up to My Silo — absolutely free. No obligation, just enjoy amazing content creators as a fan."
              : "Save your conversation and unlock more questions — it only takes a few seconds."}
          </p>

          {!signedIn ? (
            <form onSubmit={submit} className="mt-4 space-y-2.5">
              <input
                data-testid="favorite-signup-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name (optional)"
                className="w-full text-sm rounded-md border border-[#E5E5E0] bg-white px-3 py-2.5 focus:outline-none focus:border-[#4A5D4E]"
              />
              <input
                data-testid="favorite-signup-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="w-full text-sm rounded-md border border-[#E5E5E0] bg-white px-3 py-2.5 focus:outline-none focus:border-[#4A5D4E]"
              />
              <input
                data-testid="favorite-signup-password" type="password" required minLength={8} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password (8+ characters)"
                className="w-full text-sm rounded-md border border-[#E5E5E0] bg-white px-3 py-2.5 focus:outline-none focus:border-[#4A5D4E]"
              />
              <button
                type="submit" disabled={busy} data-testid="favorite-signup-submit"
                className="w-full bg-[#4A5D4E] text-white rounded-full py-3 text-sm font-semibold hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? "Creating your account…" : "Sign up free"}
              </button>
            </form>
          ) : (
            <button
              onClick={onAlreadyMemberFilled} data-testid="favorite-signup-confirm-member"
              className="mt-4 w-full bg-[#4A5D4E] text-white rounded-full py-3 text-sm font-semibold hover:bg-[#3A4A3E] flex items-center justify-center gap-2"
            >
              <CheckCircle size={16} weight="fill" /> Favorite this silo
            </button>
          )}

          {/* Home-screen nudge */}
          <div className="mt-4 rounded-lg bg-[#F0F1EE] px-3 py-2.5 flex items-start gap-2">
            <House size={16} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" />
            <p className="text-[12px] text-[#3A4A3E] leading-snug">
              While you're here, add this favorite to your phone's Home Screen for quick access.{" "}
              <button onClick={() => setHowTo(true)} data-testid="favorite-signup-seehow"
                className="text-[#4A5D4E] underline font-medium">see how now</button>
            </p>
          </div>

          <div className="mt-4 flex items-center justify-between text-[12px]">
            <button onClick={onClose} data-testid="favorite-signup-notnow"
              className="text-[#6B6A68] hover:text-[#1C1B1A]">Not right now</button>
            <button onClick={alreadyMember} data-testid="favorite-signup-already-member"
              className="text-[#6B6A68] hover:text-[#1C1B1A]">I'm already a member</button>
          </div>

          {howTo && <HomeScreenHowTo onClose={() => setHowTo(false)} />}

          <WhatIsMySiloModal open={whatIs} onClose={() => setWhatIs(false)} />

          {haha && (
            <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" data-testid="already-member-haha">
              <div className="absolute inset-0 bg-black/40" onClick={() => setHaha(false)} />
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="modal-safe relative bg-white rounded-2xl shadow-2xl border border-[#E5E5E0] p-6 max-w-xs text-center">
                <p className="text-base font-semibold text-[#1C1B1A]">Ha ha — but we'd love you to be! 😊</p>
                <p className="text-sm text-[#6B6A68] mt-1.5">It's free and takes ten seconds.</p>
                <button onClick={() => setHaha(false)} data-testid="haha-close"
                  className="mt-4 w-full text-sm rounded-full bg-[#4A5D4E] text-white py-2.5 hover:bg-[#3A4A3E]">
                  Sign me up
                </button>
              </motion.div>
            </div>
          )}
        </Shell>
      )}
    </AnimatePresence>
  );
}

// ───────────────────────────────────────────────────────────────────────────
export function OutOfInteractionsWall({ open, siloName, priceCents, onTip, onSubscribe, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <Shell onClose={onClose} testid="out-of-interactions-wall">
          <button onClick={onClose} data-testid="out-of-interactions-close"
            className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]"><X size={18} /></button>
          <div className="w-11 h-11 rounded-full bg-[#FDF1ED] flex items-center justify-center mb-3">
            <Coins size={22} weight="duotone" className="text-[#C16E5A]" />
          </div>
          <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
            You're out of questions for now.
          </h3>
          <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
            Two easy ways to keep talking with {siloName || "this silo"}:
          </p>
          <button onClick={onTip} data-testid="out-of-interactions-tip"
            className="mt-4 w-full bg-[#C16E5A] text-white rounded-full py-3 text-sm font-semibold hover:bg-[#a85946] flex items-center justify-center gap-2">
            <Coins size={16} weight="fill" /> Tip to reset your board
          </button>
          {priceCents > 0 && (
            <button onClick={onSubscribe} data-testid="out-of-interactions-subscribe"
              className="mt-2 w-full border border-[#E5E5E0] rounded-full py-3 text-sm font-semibold hover:border-[#4A5D4E] text-[#1C1B1A] flex items-center justify-center gap-2">
              <Lock size={16} weight="duotone" /> Subscribe · {fmtPrice(priceCents)}/mo for unlimited
            </button>
          )}
        </Shell>
      )}
    </AnimatePresence>
  );
}

// ───────────────────────────────────────────────────────────────────────────
export function CeilingExceededWall({ open, siloName, isGift = false, onTip, onUseTokens, hasTokens, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <Shell onClose={onClose} testid="ceiling-exceeded-wall">
          <button onClick={onClose} data-testid="ceiling-exceeded-close"
            className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]"><X size={18} /></button>
          <div className="w-11 h-11 rounded-full bg-[#FFF6E8] flex items-center justify-center mb-3">
            <Coins size={22} weight="duotone" className="text-[#8A5A00]" />
          </div>
          <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
            {isGift ? "You've enjoyed a generous free run!" : "You've used what your subscription covers."}
          </h3>
          <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
            {isGift
              ? <>{siloName || "This creator"} kindly covers your questions for free up to a point — and you've made the most of it (thank you!). A small tip keeps the conversation flowing, or switch to your earned interaction tokens.</>
              : <>Thanks for being such an active subscriber to {siloName || "this silo"}! You've reached the usage your plan includes this cycle. Keep going by tipping, or switch to your interaction tokens.</>}
          </p>
          <button onClick={onTip} data-testid="ceiling-tip"
            className="mt-4 w-full bg-[#C16E5A] text-white rounded-full py-3 text-sm font-semibold hover:bg-[#a85946] flex items-center justify-center gap-2">
            <Coins size={16} weight="fill" /> Tip to keep going
          </button>
          <button onClick={onUseTokens} disabled={!hasTokens} data-testid="ceiling-use-tokens"
            className="mt-2 w-full border border-[#E5E5E0] rounded-full py-3 text-sm font-semibold hover:border-[#4A5D4E] text-[#1C1B1A] disabled:opacity-50 flex items-center justify-center gap-2">
            <LockOpen size={16} weight="duotone" /> {hasTokens ? "Use my interaction tokens" : "No tokens left — earn or tip"}
          </button>
          <p className="text-[11px] text-[#6B6A68] mt-3 text-center">
            Your plan resets automatically at the end of the month.
          </p>
        </Shell>
      )}
    </AnimatePresence>
  );
}
